/**
 * Higher-level session helpers built on top of WbAgentClient.
 *
 * This module provides turn-level abstractions (message threading, parent
 * resolution) while keeping zero React/UI dependencies so it can be consumed
 * by any frontend framework.
 *
 * Step extraction lives in WbAgentSteps — re-exported here for convenience.
 */

import {type paths} from './generated/wb-agent-types';
import {
  TERMINAL_STATES,
  type TurnResponse,
  type TurnState,
  WbAgentApiError,
  type WbAgentClient,
} from './WbAgentClient';
import {extractSteps, type ProgressStep} from './WbAgentSteps';

// Re-export step-level API so existing consumers don't need two imports
export type {ProgressStep} from './WbAgentSteps';
export {extractSteps, summarizeToolArgs} from './WbAgentSteps';

// =============================================================================
// Constants
// =============================================================================

export const STATE_LABELS: Record<TurnState, string> = {
  queued: 'Queued',
  in_progress: 'Running',
  completed: 'Completed',
  errored: 'Error',
  cancelled: 'Cancelled',
};

// =============================================================================
// Types
// =============================================================================

export type ChatMessage =
  | {role: 'user'; text: string}
  | {
      role: 'agent';
      text: string;
      turnId: string;
      feedbackIsPositive?: boolean | null;
      feedbackReasoning?: string | null;
    }
  | {role: 'steps'; steps: ProgressStep[]}
  | {role: 'loading'; steps: ProgressStep[]};

export type Tab = 'messages' | 'history';

// =============================================================================
// Turn threading
// =============================================================================

/**
 * Final assistant text for a turn: the content of its last text-only
 * assistant message. Derived from the typed message stream rather than the
 * (removed) `final_output` response field.
 */
function finalAssistantText(turn: TurnResponse): string {
  const messages = turn.messages ?? [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (
      msg.role === 'assistant' &&
      msg.content &&
      !(msg.tool_calls && msg.tool_calls.length > 0)
    ) {
      return msg.content;
    }
  }
  return '';
}

/** Convert a list of ordered turns into ChatMessage entries. */
export function turnsToMessages(turns: TurnResponse[]): {
  messages: ChatMessage[];
  activeTurnId: string | null;
} {
  const messages: ChatMessage[] = [];
  let activeTurnId: string | null = null;
  for (const turn of turns) {
    // TODO: @doris to implement better handling of non-string prompts (e.g. images mentions)
    messages.push({
      role: 'user',
      text: typeof turn.user_prompt === 'string' ? turn.user_prompt : '',
    });
    const steps = extractSteps(turn);
    const finalText = finalAssistantText(turn);
    if (finalText) {
      if (steps.length > 0) {
        messages.push({role: 'steps', steps});
      }
      messages.push({
        role: 'agent',
        text: finalText,
        turnId: turn.id,
        feedbackIsPositive: turn.feedback_is_positive,
        feedbackReasoning: turn.feedback_reasoning,
      });
    } else if (!TERMINAL_STATES.includes(turn.state)) {
      messages.push({role: 'loading', steps});
      activeTurnId = turn.id;
    } else if (turn.state === 'errored') {
      messages.push({role: 'agent', text: 'Error', turnId: turn.id});
    }
  }
  return {messages, activeTurnId};
}

/** Strip trailing cancelled/errored exchanges from a message list. */
export function stripFailedTail(messages: ChatMessage[]): ChatMessage[] {
  const trimmed = [...messages];
  while (trimmed.length > 0) {
    const last = trimmed[trimmed.length - 1];
    if (
      last.role === 'agent' &&
      (last.text === 'Cancelled.' || last.text === 'Error')
    ) {
      trimmed.pop();
      if (trimmed.length > 0 && trimmed[trimmed.length - 1].role === 'steps') {
        trimmed.pop();
      }
      if (trimmed.length > 0 && trimmed[trimmed.length - 1].role === 'user') {
        trimmed.pop();
      }
    } else {
      break;
    }
  }
  return trimmed;
}

type ThreadTurnsParams = NonNullable<
  paths['/api/v1/threads/{thread_id}/turns']['get']['parameters']['query']
>;

/**
 * getThreadTurns lists the turns in a thread the caller owns or that was
 * shared with them. A thread that is missing or not readable has none.
 */
export async function getThreadTurns(
  client: WbAgentClient,
  threadId: string,
  params?: ThreadTurnsParams,
  signal?: AbortSignal
): Promise<TurnResponse[]> {
  try {
    return await client.get<TurnResponse[]>(
      `/threads/${threadId}/turns`,
      params,
      signal
    );
  } catch (err) {
    if (err instanceof WbAgentApiError && err.status === 404) {
      return [];
    }
    throw err;
  }
}

/**
 * Find the thread_id for the most recently active conversation.
 * Uses the most recent turn (of any kind) and returns its thread.
 */
export async function getMostRecentSessionId(
  client: WbAgentClient
): Promise<string | null> {
  const mostRecent = await client
    .get('/turns', {sort_by: 'created_at', sort_desc: true})
    .catch(() => [] as TurnResponse[]);
  return mostRecent.length > 0 ? mostRecent[0].thread_id : null;
}
